import { useState } from "react";
import { useLocation } from "react-router-dom";
import { z } from "zod";
import Navbar from "src/components/Navbar";
import Footer from "src/components/Footer";
import { Card, CardContent } from "src/components/ui/Card";
import { Button } from "src/components/ui/Button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "src/components/ui/Tabs";
import { Search, Sparkles, CheckCircle } from "lucide-react";
import useScrollToTop from "src/hooks/useScrollToTop";
import { emailSchema, phoneNumberSchema, nameSchema } from "src/services/errorHandler";
import StudentParentForm from "src/components/forms/StudentParentForm";
import TutorSearch from "src/components/tutor/Search";
import { supabaseServiceRole } from "src/lib/supabaseServiceClient";
import { useToast } from "src/hooks/use-toast";
import { DEMO_BOOKING_CC_RECIPIENTS, emailService } from "src/services/emailService";

// Demo booking form schema
const demoBookingSchema = z.object({
  parentName: nameSchema,
  studentName: nameSchema,
  parentPhone: phoneNumberSchema,
  email: emailSchema,
  grade: z.string().min(1, { message: "Please select a grade" }),
  primaryGoals: z.array(z.string()).min(1, { message: "Please select at least one goal" }),
});

const TutorSearchRedesigned = () => {
  const location = useLocation();
  const initialTab = (location.state as any)?.activeTab === 'search' ? 'search' : 'match';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [formData, setFormData] = useState({
    parentName: "",
    studentName: "",
    parentPhone: "",
    email: "",
    grade: "",
    primaryGoals: [],
    services: [],
    isoCountryCode: "",
    phoneIsdCode: "",
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [confirmation, setConfirmation] = useState<{
    id: string;
    parentName: string;
    studentName: string;
    email: string;
    phone: string;
    grade?: string;
    primaryGoals: string[];
  } | null>(null);

  useScrollToTop();

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Real-time validation - clear errors when valid, show errors when invalid
    try {
      if (field === 'parentName' || field === 'studentName') {
        nameSchema.parse(value);
        // Clear error if validation passes
        setFormErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      } else if (field === 'parentPhone') {
        phoneNumberSchema.parse(value);
        setFormErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      } else if (field === 'email') {
        emailSchema.parse(value);
        setFormErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      } else if (field === 'grade') {
        if (value.trim() !== '') {
          setFormErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[field];
            return newErrors;
          });
        }
      }
    } catch (error) {
      if (error instanceof z.ZodError && value.trim() !== '') {
        // Only show error if field is not empty
        const errorMessage = error.errors[0]?.message;
        if (errorMessage) {
          setFormErrors(prev => ({
            ...prev,
            [field]: errorMessage
          }));
        }
      } else if (value.trim() === '') {
        // Clear error if field is empty (don't show validation errors for empty fields)
        setFormErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    }
  };

  const handleCheckboxChange = (field: string, value: string, checked: boolean) => {
    const updatedArray = checked 
      ? [...(formData[field] as string[]), value]
      : (formData[field] as string[]).filter(item => item !== value);
    
    setFormData(prev => ({
      ...prev,
      [field]: updatedArray
    }));

    // Real-time validation for primaryGoals
    if (field === 'primaryGoals') {
      if (updatedArray.length > 0) {
        // Clear error if at least one goal is selected
        setFormErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      } else {
        // Show error if no goals are selected
        setFormErrors(prev => ({
          ...prev,
          [field]: "Please select at least one goal"
        }));
      }
    }
  };

  const handleBookDemo = async () => {
    // Clear previous errors
    setFormErrors({});

    try {
      // Validate form data
      const validatedData = demoBookingSchema.parse(formData);

      setIsSubmitting(true);

      // Prepare payload matching DB columns (snake_case)
      const payload = {
        parent_name: validatedData.parentName.trim(),
        student_name: validatedData.studentName.trim(),
        parent_phone: validatedData.parentPhone.trim(),
        email: validatedData.email.trim().toLowerCase(),
        grade: validatedData.grade || null,
        primary_goals: (validatedData.primaryGoals && validatedData.primaryGoals.length > 0)
          ? validatedData.primaryGoals
          : null,
        iso_country_code: formData.isoCountryCode || null,
        phone_isd_code: formData.phoneIsdCode || null,
      } as const;

      const { data, error } = await supabaseServiceRole
        .from("demo_requests")
        .insert(payload)
        .select("id")
        .single();

      if (error) {
        // Log full error for diagnostics
        console.error("Error creating demo request:", error);
        throw new Error(error.message || "Failed to submit demo request");
      }

      // Success UX
      toast({
        title: "Request received",
        description: "Thanks! Your request was submitted successfully.",
      });

      // Open confirmation dialog with submitted details
      setConfirmation({
        id: data.id,
        parentName: validatedData.parentName,
        studentName: validatedData.studentName,
        email: validatedData.email,
        phone: validatedData.parentPhone,
        grade: validatedData.grade,
        primaryGoals: validatedData.primaryGoals || [],
      });
      setIsConfirmationOpen(true);

      // Send booking confirmation email for "Let Us Match You" flow
      try {
        const formattedParentPhone = `${formData.phoneIsdCode ? `(${formData.phoneIsdCode}) ` : ''}${validatedData.parentPhone}${formData.isoCountryCode ? ` (${formData.isoCountryCode})` : ''}`.trim();
        const emailResult = await emailService.sendTemplateEmail({
          channel: 'booking',
          template: 'demo_booking_confirmation_match_flow',
          to: validatedData.email,
          cc: DEMO_BOOKING_CC_RECIPIENTS,
          subject: `Thanks for requesting a free demo`,
          data: {
            parent_name: validatedData.parentName,
            student_name: validatedData.studentName,
            parent_phone: formattedParentPhone,
            email: validatedData.email,
            selected_grade: validatedData.grade,
            primary_goals: (validatedData.primaryGoals || []).join(', ')
          }
        });
        if (!emailResult.success) {
          console.warn('Booking confirmation email failed:', emailResult.error, emailResult.details);
        }
      } catch (e) {
        console.warn('Error sending booking confirmation email:', e);
      }

      // Optional: reset form
      setFormData({
        parentName: "",
        studentName: "",
        parentPhone: "",
        email: "",
        grade: "",
        primaryGoals: [],
        services: [],
        isoCountryCode: "",
        phoneIsdCode: "",
      });

    } catch (error: any) {
      if (error instanceof z.ZodError) {
        const errors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path.length > 0) {
            errors[err.path[0] as string] = err.message;
          }
        });
        setFormErrors(errors);
        toast({
          title: "Please fix the highlighted fields",
          description: "Some information is missing or invalid.",
          variant: "destructive",
        });
      } else {
        console.error("Demo request submission failed:", error);
        toast({
          title: "Submission failed",
          description: error?.message || "There was an error submitting your request. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Hero Section */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Book a free demo
            </h1>
            <div className="flex items-center justify-center gap-8 text-lg text-gray-700">
              <div className="flex items-center gap-2">
                <Search className="h-5 w-5 text-purple-600" />
                <span>Let us Match You</span>
              </div>
              <span className="text-2xl">OR</span>
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-600" />
                <span>Search and Book Yourself</span>
              </div>
            </div>
          </div>

          {/* Main Content Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 max-w-2xl mx-auto mb-8 h-14 p-1 bg-white border border-gray-200 rounded-lg shadow-sm">
              <TabsTrigger
                value="match"
                className="flex items-center justify-center gap-2 h-full px-6 py-3 text-sm font-medium rounded-md transition-all duration-200 data-[state=active]:bg-purple-600 data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:text-gray-900 data-[state=inactive]:hover:bg-gray-50"
              >
                <Sparkles className="h-4 w-4" />
                <span>Let Us Match You</span>
              </TabsTrigger>
              <TabsTrigger
                value="search"
                className="flex items-center justify-center gap-2 h-full px-6 py-3 text-sm font-medium rounded-md transition-all duration-200 data-[state=active]:bg-purple-600 data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:text-gray-900 data-[state=inactive]:hover:bg-gray-50"
              >
                <Search className="h-4 w-4" />
                <span>Find Your Tutor</span>
              </TabsTrigger>
            </TabsList>

            {/* Let Us Match You Tab */}
            <TabsContent value="match">
              <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,0.82fr)_minmax(0,1.18fr)]">
                {/* Left Side - Benefits */}
                <Card className="relative w-fit max-w-full justify-self-start overflow-visible rounded-none border-0 bg-transparent shadow-none">
                  <svg
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 h-full w-full"
                    viewBox="0 0 560 340"
                    preserveAspectRatio="none"
                  >
                    <path
                      d="M44 10 C78 4 109 14 143 9 C177 4 210 14 244 9 C278 4 312 14 346 9 C380 4 414 14 448 9 C482 4 510 12 526 21 C543 31 550 48 549 67 C548 96 554 121 549 149 C544 177 554 204 549 232 C544 260 551 284 540 302 C530 319 510 325 483 319 C450 313 418 325 385 319 C352 313 319 325 286 319 C253 313 220 325 187 319 C154 313 121 325 88 319 C59 324 35 315 21 299 C7 283 11 259 10 230 C9 201 3 175 10 147 C17 119 5 93 10 65 C15 37 24 17 44 10 Z"
                      fill="white"
                      stroke="#d8b4fe"
                      strokeWidth="2"
                      vectorEffect="non-scaling-stroke"
                    />
                  </svg>
                  <CardContent className="relative z-10 p-8">
                    <div className="mb-5 flex items-center gap-3">
                      <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-purple-100">
                        <Sparkles className="h-5 w-5 text-purple-600" />
                      </div>
                      <h2 className="text-xl font-bold text-gray-900">Let Us Match You</h2>
                    </div>
                    <p className="mb-6 max-w-sm text-sm leading-6 text-gray-600">
                      Share your needs and we'll pair you with the right tutor for your demo.
                    </p>
                    <div className="space-y-3 text-sm">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="h-5 w-5 shrink-0 text-green-500" />
                        <span className="text-gray-700">Save time searching</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircle className="h-5 w-5 shrink-0 text-green-500" />
                        <span className="text-gray-700">Expert matching for your needs</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircle className="h-5 w-5 shrink-0 text-green-500" />
                        <span className="text-gray-700">Minimal effort required</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Student or parent info - Form */}
                <Card className="border border-gray-200 shadow-lg">
                  <CardContent className="p-6">
                    <StudentParentForm
                      formData={{
                        parentName: formData.parentName,
                        studentName: formData.studentName,
                        parentPhone: formData.parentPhone,
                        email: formData.email,
                        grade: formData.grade,
                        primaryGoals: formData.primaryGoals,
                        isoCountryCode: formData.isoCountryCode,
                        phoneIsdCode: formData.phoneIsdCode,
                      }}
                      formErrors={formErrors}
                      onInputChange={handleInputChange}
                      onCheckboxChange={handleCheckboxChange}
                      showGradeSelection={true}
                      showPrimaryGoals={true}
                    />
                    
                    <div className="mt-4">
                      <Button
                        onClick={handleBookDemo}
                        disabled={isSubmitting}
                        className="w-full text-white py-3 text-lg font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                        style={{ backgroundColor: '#ff7f52' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e6704a'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ff7f52'}
                      >
                        {isSubmitting ? 'Submitting...' : 'BOOK A FREE DEMO'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Find Your Tutor Tab */}
            <TabsContent value="search">
              <TutorSearch />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Confirmation Modal */}
      {isConfirmationOpen && confirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setIsConfirmationOpen(false)} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="border-b px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">Demo request submitted</h3>
              <p className="text-sm text-gray-600 mt-1">We have received your details. Our team will contact you shortly to schedule your free demo.</p>
            </div>
            <div className="px-6 py-4 space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Parent's Name</p>
                  <p className="font-medium text-gray-900 break-words">{confirmation.parentName}</p>
                </div>
                <div>
                  <p className="text-gray-500">Student's Name</p>
                  <p className="font-medium text-gray-900 break-words">{confirmation.studentName}</p>
                </div>
                <div>
                  <p className="text-gray-500">Email</p>
                  <p className="font-medium text-gray-900 break-words">{confirmation.email}</p>
                </div>
                <div>
                  <p className="text-gray-500">Phone</p>
                  <p className="font-medium text-gray-900 break-words">{confirmation.phone}</p>
                </div>
                {confirmation.grade && (
                  <div className="col-span-2">
                    <p className="text-gray-500">Grade</p>
                    <p className="font-medium text-gray-900 break-words">{confirmation.grade}</p>
                  </div>
                )}
                {confirmation.primaryGoals && confirmation.primaryGoals.length > 0 && (
                  <div className="col-span-2">
                    <p className="text-gray-500">Primary Goals</p>
                    <ul className="list-disc list-inside text-gray-900">
                      {confirmation.primaryGoals.map((g, idx) => (
                        <li key={idx} className="break-words">{g}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="col-span-2">
                  <p className="text-gray-500">Reference ID</p>
                  <p className="font-mono text-sm text-gray-900 break-all">{confirmation.id}</p>
                </div>
              </div>
            </div>
            <div className="border-t px-6 py-4 flex justify-end gap-2">
              <Button
                className="px-4"
                onClick={() => setIsConfirmationOpen(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default TutorSearchRedesigned;
